import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { logout } from '../actions/userActions';
import {
    Box,
    Grid,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Avatar,
    CircularProgress,
    Paper,
    Divider,
    InputAdornment,
    Badge,
    Tooltip,
    Alert,
    Skeleton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Person as PersonIcon,
    EventSeat as SeatIcon,
    Search as SearchIcon,
    Verified as VerifiedIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    ScreenRotation as RotateIcon,
    PlayArrow as StartIcon,
    Stop as StopIcon,
    Timer as TimerIcon,
    Phone as PhoneIcon,

    PersonAdd as PersonAddIcon,
    MonetizationOn as MoneyIcon,
    Logout as LogoutIcon,
    KeyboardArrowDown as PullIcon,
    Pause as PauseIcon,
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { gsap } from 'gsap';
import { useRef } from 'react';
import TableTimer from '../components/TableTimer';
import DealerTableGrid from '../components/DealerTableGrid';
// import TableTransferDialog from '../components/TableTransferDialog'; // table transfer disabled

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';

// Clean API_URL - Remove trailing /api if present because the routes below append it again
// Or simpler: Just define a Base URL that does NOT have the api suffix
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
// Remove trailing slash if present
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

const DealerManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // State
    const [isLandscape, setIsLandscape] = useState(true);
    const [isMobile, setIsMobile] = useState(true); // Default true to avoid flash, check in effect
    const [checkedDevice, setCheckedDevice] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);
    const [selectedClub, setSelectedClub] = useState(null);
    const [tables, setTables] = useState([]);
    const [tablesLoaded, setTablesLoaded] = useState(false);
    const [tableSearch, setTableSearch] = useState('');
    // const [tableToTransfer, setTableToTransfer] = useState(null); // table transfer disabled
    // The open table lives in the URL so the device back button returns to the table list
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTableId = searchParams.get('table');
    const [selectedSeat, setSelectedSeat] = useState(null);
    const [selectedSeatContext, setSelectedSeatContext] = useState(null);
    const [dealerSession, setDealerSession] = useState(null);
    const [playerSessions, setPlayerSessions] = useState({}); // Map of playerId -> session data
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Modals
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [playerDetailModalOpen, setPlayerDetailModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [playerVerification, setPlayerVerification] = useState(null);

    // Registration Modal State
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [registerStep, setRegisterStep] = useState(1); // 1: Phone/Name, 2: OTP
    const [registerData, setRegisterData] = useState({ phoneNumber: '', name: '', otp: '' });

    // Buy-In & Cash-Out State
    const [buyInModalOpen, setBuyInModalOpen] = useState(false);
    const [cashOutModalOpen, setCashOutModalOpen] = useState(false);
    const [rebuyModalOpen, setRebuyModalOpen] = useState(false); // New Rule: Re-top-up
    const [transactionAmount, setTransactionAmount] = useState('');
    const [pendingPlayerToSeat, setPendingPlayerToSeat] = useState(null);
    const [seatToCashOut, setSeatToCashOut] = useState(null);
    const [seatToRebuy, setSeatToRebuy] = useState(null);

    // Pull-to-refresh state
    const [isPulling, setIsPulling] = useState(false);
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const scrollContainerRef = useRef(null);
    const pullThreshold = 80; // Distance in pixels to trigger refresh

    const resetRegistrationState = useCallback(() => {
        setRegisterStep(1);
        setRegisterData({ phoneNumber: '', name: '', otp: '' });
    }, []);

    const handleOpenRegistrationModal = useCallback(() => {
        resetRegistrationState();
        setRegisterModalOpen(true);
    }, [resetRegistrationState]);

    const handleCloseRegistrationModal = useCallback(() => {
        setRegisterModalOpen(false);
        resetRegistrationState();
    }, [resetRegistrationState]);

    // Device and Orientation Check
    useEffect(() => {
        const checkDevice = () => {
            // Mobile detection
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
            const isMobileDevice = mobileRegex.test(userAgent);
            setIsMobile(isMobileDevice);

            // Orientation detection
            // simple check: width > height
            const isLandscapeMode = window.innerWidth > window.innerHeight;
            setIsLandscape(isLandscapeMode);

            setCheckedDevice(true);
        };

        checkDevice();
        window.addEventListener('resize', checkDevice);
        window.addEventListener('orientationchange', checkDevice);

        return () => {
            window.removeEventListener('resize', checkDevice);
            window.removeEventListener('orientationchange', checkDevice);
        };
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(`${API_URL}/api/dealer/dashboard`, config);
            setDashboard(data.data);
            setDealerSession(data.data.activeSession);

            if (data.data.clubs?.length > 0 && !selectedClub) {
                // Prefer the first club with tables assigned to this dealer; the order of
                // assignedClubs alone would land on a club with nothing to show.
                const clubs = data.data.clubs;
                setSelectedClub(clubs.find((c) => c.tableCount > 0) || clubs[0]);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, [userInfo, selectedClub]);

    // Fetch tables for selected club
    const fetchTables = useCallback(async () => {
        if (!selectedClub) return;

        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(
                `${API_URL}/api/dealer/tables/${selectedClub._id}`,
                config
            );
            setTables(data.data);
            setTablesLoaded(true);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load tables');
        }
    }, [userInfo, selectedClub]);

    // Fetch active sessions
    const fetchPlayerSessions = useCallback(async () => {
        if (!selectedClub) return;
        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(
                `${API_URL}/api/dealer/active-sessions?clubId=${selectedClub?._id}`,
                config
            );

            // Convert array to map for easy lookup: playerId -> session
            const sessionsMap = {};
            data.data.forEach(item => {
                if (item.player && item.session) {
                    sessionsMap[item.player._id] = item.session;
                }
            });
            setPlayerSessions(sessionsMap);
        } catch (error) {
            console.error('Failed to fetch sessions', error);
        }
    }, [userInfo, selectedClub]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    // Keep fullscreen icon in sync if the user exits via Esc / OS gesture
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const handleToggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.().catch(() => {
                toast.error('Fullscreen is not supported on this browser');
            });
        } else {
            document.exitFullscreen?.();
        }
    };

    // Opening from the list pushes a history entry, so closing pops it; a table opened
    // straight from a link (e.g. after a page refresh) replaces instead.
    const openedFromListRef = useRef(false);
    const openTable = (table) => {
        openedFromListRef.current = true;
        setSearchParams({ table: table._id });
    };
    const closeTable = () => {
        if (openedFromListRef.current) {
            navigate(-1);
        } else {
            setSearchParams({}, { replace: true });
        }
    };
    useEffect(() => {
        if (!selectedTableId) openedFromListRef.current = false;
    }, [selectedTableId]);

    // Turn the screen to landscape while a table is open, and back for the list.
    // Browsers only allow this in fullscreen (mostly Android); elsewhere the rotate prompt shows.
    useEffect(() => {
        const orientation = window.screen?.orientation;
        if (!orientation?.lock) return;
        if (selectedTableId) {
            orientation.lock('landscape').catch(() => {});
        } else {
            orientation.unlock?.();
        }
    }, [selectedTableId]);

    // Table transfer disabled: not needed for now
    // // After a transfer the table stays on our list, shown as running by the new dealer
    // const handleTableTransferred = (table) => {
    //     setTableToTransfer(null);
    //     if (selectedTableId === table._id) closeTable();
    //     fetchTables();
    // };

    // A table is shown only once the server has given us its lock, so two dealers
    // never run the same table at once.
    const [claimedTableId, setClaimedTableId] = useState(null);
    useEffect(() => {
        if (!selectedTableId || selectedTableId === claimedTableId || !userInfo?.token) return;
        let cancelled = false;
        axios
            .post(
                `${API_URL}/api/dealer/tables/${selectedTableId}/open`,
                {},
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            )
            .then(() => {
                if (!cancelled) {
                    setClaimedTableId(selectedTableId);
                    fetchTables();
                }
            })
            .catch((error) => {
                if (cancelled) return;
                toast.error(error.response?.data?.message || 'Could not open this table');
                closeTable();
                fetchTables();
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTableId, claimedTableId, userInfo]);

    const selectedTable = selectedTableId && selectedTableId === claimedTableId
        ? tables.find((t) => t._id === selectedTableId)
        : null;

    // Table in the URL is gone (transferred, unassigned or deactivated): fall back to the list
    useEffect(() => {
        if (tablesLoaded && selectedTableId && !tables.some((t) => t._id === selectedTableId)) {
            setSearchParams({}, { replace: true });
        }
    }, [tablesLoaded, selectedTableId, tables, setSearchParams]);

    // GSAP Animation for Tables
    useEffect(() => {
        if (tables.length > 0) {
            gsap.killTweensOf('.table-card');
            gsap.fromTo(
                '.table-card',
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power3.out' }
            );
        }
    }, [tables, selectedTableId]);

    useEffect(() => {
        if (selectedClub) {
            fetchTables();
            fetchPlayerSessions();

            // Auto-start dealer session when club is selected (if not already active)
            const autoStartSession = async () => {
                if (!dealerSession && userInfo?.token) {
                    try {
                        const config = {
                            headers: { Authorization: `Bearer ${userInfo?.token}` },
                        };
                        const { data } = await axios.post(
                            `${API_URL}/api/dealer/start-session`,
                            { clubId: selectedClub._id },
                            config
                        );
                        setDealerSession(data.data);
                        console.log('✅ Dealer session auto-started');
                    } catch (error) {
                        // Session might already exist - fetch dashboard to get it
                        console.log('Session start info:', error.response?.data?.message || error.message);
                    }
                }
            };
            autoStartSession();
        }
    }, [selectedClub, fetchTables, fetchPlayerSessions, dealerSession, userInfo]);

    // Poll for "another dealer is trying to log in" notifications while this dealer is active
    useEffect(() => {
        if (!userInfo?.token || !dealerSession) return;

        const checkLoginAttempt = async () => {
            try {
                const config = {
                    headers: { Authorization: `Bearer ${userInfo?.token}` },
                };
                const { data } = await axios.get(`${API_URL}/api/dealer/login-attempt`, config);
                const attempt = data?.data;
                if (attempt) {
                    toast.warning(
                        `${attempt.name} is trying to open ${attempt.tableName}`,
                        { autoClose: 8000 }
                    );
                }
            } catch (error) {
                // Silent - this is a background check, don't disrupt the dealer's workflow
            }
        };

        const intervalId = setInterval(checkLoginAttempt, 8000);
        return () => clearInterval(intervalId);
    }, [userInfo, dealerSession]);

    // Pull-to-refresh handler
    const handlePullRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                fetchDashboard(),
                fetchTables(),
                fetchPlayerSessions()
            ]);
            toast.success('Refreshed!');
        } catch (error) {
            toast.error('Refresh failed');
        } finally {
            setIsRefreshing(false);
        }
    }, [fetchDashboard, fetchTables, fetchPlayerSessions]);

    // Touch-based pull-to-refresh
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        let startY = 0;
        let currentPull = 0;

        const handleTouchStart = (e) => {
            if (container.scrollTop === 0) {
                startY = e.touches[0].clientY;
                setIsPulling(true);
            }
        };

        const handleTouchMove = (e) => {
            if (!isPulling || container.scrollTop > 0) return;

            currentPull = Math.max(0, e.touches[0].clientY - startY);
            setPullDistance(currentPull);
        };

        const handleTouchEnd = () => {
            if (currentPull >= pullThreshold && !isRefreshing) {
                handlePullRefresh();
            }
            setIsPulling(false);
            setPullDistance(0);
            currentPull = 0;
        };

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: true });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isPulling, isRefreshing, handlePullRefresh, pullThreshold]);

    // Start dealer session
    const handleStartSession = async () => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.post(
                `${API_URL}/api/dealer/start-session`,
                { clubId: selectedClub._id },
                config
            );
            setDealerSession(data.data);
            toast.success('Dealer session started');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to start session');
        }
    };

    // Control Player Session (Start/Pause/Resume)
    const handleControlSession = async (playerId, action) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            await axios.post(
                `${API_URL}/api/dealer/control-player-session`,
                { playerId, action },
                config
            );
            toast.success(`Session ${action}ed`);
            fetchPlayerSessions(); // Refresh sessions
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${action} session`);
        }
    };

    // Search players
    const handleSearchPlayers = useCallback(async (query = '') => {
        if (!selectedClub?._id) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }

        // If query is provided but short, don't search (unless it's empty for default list)
        if (query && query.length > 0 && query.length < 3) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }

        try {
            setSearchLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const params = new URLSearchParams({ clubId: selectedClub._id });
            if (query) params.set('query', query);
            const url = `${API_URL}/api/dealer/search-players?${params.toString()}`;

            const { data } = await axios.get(url, config);
            const players = Array.isArray(data.data) ? data.data : [];
            // Keep available players ahead of players who are already seated.
            setSearchResults(
                players
                    .map((player, index) => ({ player, index }))
                    .sort((a, b) => {
                        const getPriority = (player) => {
                            if (player.isSeated) return 1;
                            return 0;
                        };
                        return getPriority(a.player) - getPriority(b.player) || a.index - b.index;
                    })
                    .map(({ player }) => player)
            );
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to search players');
        } finally {
            setSearchLoading(false);
        }
    }, [userInfo, selectedClub?._id]);

    // Fetch default players when modal opens
    useEffect(() => {
        if (searchModalOpen) {
            handleSearchPlayers('');
        } else {
            setSearchResults([]); // Clear when closed
            setSearchQuery('');
        }
    }, [searchModalOpen, handleSearchPlayers]);

    // Initiate Seating (for hour tracking - no balance check needed)
    const handleInitiateSeat = (player) => {
        // No balance check - system is for hour tracking only
        setPendingPlayerToSeat(player);
        setTransactionAmount('');
        setBuyInModalOpen(true);
        setSearchModalOpen(false); // Close search modal
    };

    // Confirm Seating with Buy-In
    const handleConfirmSeat = async () => {
        if (!selectedSeat || !pendingPlayerToSeat) return;

        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };

            const payload = {
                tableId: selectedSeat.tableId,
                seatNumber: selectedSeat.seatNumber,
                playerId: pendingPlayerToSeat._id,
                buyInAmount: 0 // No buy-in for time tracking
            };

            const { data } = await axios.post(
                `${API_URL}/api/dealer/seat-player`,
                payload,
                config
            );

            toast.success(data.message);
            setBuyInModalOpen(false);
            setPendingPlayerToSeat(null);
            setSelectedSeat(null);
            fetchTables();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to seat player');
        }
    };

    // Initiate Remove/Cash-Out
    const handleInitiateRemove = (tableId, seatNumber, player) => {
        setSelectedSeatContext({ tableId, seatNumber, playerName: player.name });
        setSeatToCashOut({ tableId, seatNumber, playerName: player.name });
        setTransactionAmount('');
        setCashOutModalOpen(true);
        setPlayerDetailModalOpen(false);
    };

    // Confirm Cash-Out
    const handleConfirmCashOut = async () => {
        if (!seatToCashOut) return;

        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };

            const payload = {
                tableId: seatToCashOut.tableId,
                seatNumber: seatToCashOut.seatNumber,
                cashOutAmount: null, // No cash-out amount
                reason: 'Session Stopped'
            };

            const { data } = await axios.post(
                `${API_URL}/api/dealer/remove-player`,
                payload,
                config
            );

            const writtenOffToMissing = Number(data?.data?.chipSettlement?.writtenOffToMissing || 0);
            const successMessage = writtenOffToMissing > 0
                ? `${data.message} Missing +₹${writtenOffToMissing}`
                : data.message;
            toast.success(successMessage);
            setCashOutModalOpen(false);
            setSeatToCashOut(null);
            setSelectedSeatContext(null);
            fetchTables();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to remove player');
        }
    };

    // Initiate Rebuy (Add Chips)
    const handleInitiateRebuy = (tableId, seatNumber, player) => {
        setSelectedSeatContext({ tableId, seatNumber, playerName: player.name });
        setSeatToRebuy({ tableId, seatNumber, playerName: player.name });
        setTransactionAmount('');
        setRebuyModalOpen(true);
        setPlayerDetailModalOpen(false);
    };

    // Confirm Rebuy
    const handleConfirmRebuy = async () => {
        if (!seatToRebuy) return;

        try {
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };

            const payload = {
                tableId: seatToRebuy.tableId,
                seatNumber: seatToRebuy.seatNumber,
                amount: parseFloat(transactionAmount) || 0
            };

            const { data } = await axios.post(
                `${API_URL}/api/dealer/rebuy-player`,
                payload,
                config
            );

            toast.success(data.message);
            setRebuyModalOpen(false);
            setSeatToRebuy(null);
            setTransactionAmount('');
            fetchTables();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to rebuy');
        }
    };

    // Get player verification and refresh session status
    const handleViewPlayerDetails = async (player, seatContext = null) => {
        try {
            // Refresh sessions first to ensure button state is correct
            await fetchPlayerSessions();

            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };
            const { data } = await axios.get(
                `${API_URL}/api/dealer/verify-player/${player._id}`,
                config
            );
            setPlayerVerification(data.data);
            setSelectedPlayer(player);
            setSelectedSeatContext(
                seatContext?.tableId && seatContext?.seatNumber
                    ? {
                        tableId: seatContext.tableId,
                        seatNumber: seatContext.seatNumber,
                        playerName: player.name,
                    }
                    : null
            );
            setPlayerDetailModalOpen(true);
        } catch (error) {
            toast.error('Failed to load player details');
        }
    };

    // Register Player Handlers
    const handleRegisterPhoneChange = (e) => {
        const raw = e.target.value;
        let digits = raw.replace(/\D/g, '');
        // Allow pasting a number that already carries the +91 country code
        if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
        if (digits.length > 10) {
            toast.error('Phone number cannot be more than 10 digits', { toastId: 'phone-too-long' });
            digits = digits.slice(0, 10);
        }
        setRegisterData({ ...registerData, phoneNumber: digits });
    };

    const handleInitiateRegistration = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${userInfo?.token}` } };
            const { data } = await axios.post(
                `${API_URL}/api/dealer/register-player`,
                { phoneNumber: registerData.phoneNumber, name: registerData.name },
                config
            );
            toast.success(data.message);
            if (data.devOtp) toast.info(`Dev OTP: ${data.devOtp}`); // Remove in prod
            setRegisterStep(2);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send OTP');
        }
    };

    const handleConfirmRegistration = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${userInfo?.token}` } };
            const { data } = await axios.post(
                `${API_URL}/api/dealer/confirm-registration`,
                { phoneNumber: registerData.phoneNumber, otp: registerData.otp },
                config
            );
            toast.success('Player registered successfully');
            handleCloseRegistrationModal();

            // Auto-select for seating if triggered from search
            if (selectedSeat) {
                handleInitiateSeat(data.data);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to verify OTP');
        }
    };

    const resolveSelectedPlayerSeatContext = useCallback(() => {
        if (selectedSeatContext?.tableId && selectedSeatContext?.seatNumber) {
            return selectedSeatContext;
        }

        let foundSeatContext = null;
        if (selectedPlayer?._id) {
            tables.forEach((table) => {
                if (foundSeatContext) return;
                const seat = (table.seats || []).find((s) => s.currentPlayer?._id === selectedPlayer._id);
                if (seat) {
                    foundSeatContext = {
                        tableId: table._id,
                        seatNumber: seat.seatNumber,
                        playerName: selectedPlayer.name,
                    };
                }
            });
        }

        if (foundSeatContext) {
            setSelectedSeatContext(foundSeatContext);
        }

        return foundSeatContext;
    }, [selectedPlayer, selectedSeatContext, tables]);

    const handleStopSessionFromDetails = () => {
        const seatContext = resolveSelectedPlayerSeatContext();
        if (!seatContext || !selectedPlayer) {
            toast.error("Could not find player seat");
            return;
        }
        handleInitiateRemove(seatContext.tableId, seatContext.seatNumber, selectedPlayer);
    };

    const handleInitiateRebuyFromDetails = () => {
        const seatContext = resolveSelectedPlayerSeatContext();
        if (!seatContext || !selectedPlayer) {
            toast.error("Could not find player seat");
            return;
        }
        handleInitiateRebuy(seatContext.tableId, seatContext.seatNumber, selectedPlayer);
    };

    // Helper: Format elapsed time from session
    const formatElapsedTime = (session) => {
        if (!session?.sessionStart) return '';
        const start = new Date(session.sessionStart);
        const now = new Date();
        let diffMs = now - start;

        // Account for paused time
        if (session.totalPausedDuration) {
            diffMs -= session.totalPausedDuration * 60 * 1000;
        }

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    // Seat component with player name and elapsed time
    const SeatComponent = ({ seat, table }) => {
        const isOccupied = seat.status === 'Occupied';
        const isAvailable = seat.status === 'Available';
        const isReserved = seat.status === 'Reserved';

        const playerSession = isOccupied && seat.currentPlayer ? playerSessions[seat.currentPlayer._id] : null;
        const isActive = playerSession?.status === 'active';
        const isPaused = playerSession?.status === 'paused';
        const elapsedTime = playerSession ? formatElapsedTime(playerSession) : '';

        // Get player initials (first 2 chars of name)
        const playerInitials = seat.currentPlayer?.name?.slice(0, 2)?.toUpperCase() || 'U';
        const playerName = seat.currentPlayer?.name?.split(' ')[0]?.slice(0, 6) || 'Unknown';

        let borderColor = 'white';
        let boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

        if (isActive) {
            borderColor = '#22c55e'; // Green
            boxShadow = '0 0 10px #22c55e';
        } else if (isPaused) {
            borderColor = '#eab308'; // Yellow
            boxShadow = '0 0 10px #eab308';
        }

        const bgColor = isOccupied
            ? '#3b82f6' // Blue for occupied (easier to read text)
            : isReserved
                ? '#f97316'
                : '#22c55e'; // Green for available

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35 }}>
                <Tooltip
                    title={
                        isOccupied
                            ? `${seat.currentPlayer?.name || 'Unknown'} - ${isActive ? 'Active' : isPaused ? 'Paused' : 'Seated'}`
                            : isReserved
                                ? 'Reserved'
                                : 'Click to seat player'
                    }
                >
                    <Box
                        onClick={() => {
                            if (isOccupied && seat.currentPlayer) {
                                handleViewPlayerDetails(seat.currentPlayer, {
                                    tableId: table._id,
                                    seatNumber: seat.seatNumber,
                                });
                            } else if (isAvailable) {
                                setSelectedSeatContext(null);
                                setSelectedSeat({ tableId: table._id, seatNumber: seat.seatNumber });
                                setSearchModalOpen(true);
                            }
                        }}
                        sx={{
                            width: 'clamp(36px, 6vw, 56px)',
                            height: 'clamp(36px, 6vw, 56px)',
                            borderRadius: '50%',
                            backgroundColor: bgColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: `clamp(2px, 0.5vw, 3px) solid ${borderColor}`,
                            boxShadow: `${boxShadow}, 0 5px 12px rgba(0,0,0,0.5)`,
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'scale(1.12) translateY(-2px)',
                            },
                            position: 'relative',
                        }}
                    >
                        {/* Pause indicator */}
                        {isPaused && (
                            <Box sx={{ position: 'absolute', top: -5, right: -5, bgcolor: 'var(--cm-warning)', borderRadius: '50%', p: '2px' }}>
                                <TimerIcon sx={{ fontSize: 12, color: 'black' }} />
                            </Box>
                        )}

                        {/* Elapsed time badge */}
                        {isOccupied && elapsedTime && (
                            <Box sx={{
                                position: 'absolute',
                                top: -8,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: isActive ? '#22c55e' : '#eab308',
                                borderRadius: '4px',
                                px: 0.5,
                                py: 0.1,
                            }}>
                                <Typography sx={{ fontSize: '0.55rem', color: 'white', fontWeight: 'bold' }}>
                                    {elapsedTime}
                                </Typography>
                            </Box>
                        )}

                        {isOccupied ? (
                            <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 'clamp(0.65rem, 1.6vw, 0.85rem)' }}>
                                {playerInitials}
                            </Typography>
                        ) : (
                            <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 'clamp(0.75rem, 1.8vw, 1rem)' }}>
                                {seat.seatNumber}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>

                {/* Player name below seat */}
                {isOccupied && (
                    <Typography sx={{
                        color: 'white',
                        fontSize: 'clamp(0.5rem, 1.1vw, 0.6rem)',
                        fontWeight: 700,
                        textAlign: 'center',
                        maxWidth: 'clamp(52px, 9vw, 76px)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        bgcolor: 'rgba(11,11,12,0.88)',
                        border: '1px solid rgba(243,234,219,0.35)',
                        borderRadius: 5,
                        px: 0.75,
                        py: 0.15,
                        boxShadow: '0 3px 8px rgba(0,0,0,0.35)',
                    }}>
                        {playerName}
                    </Typography>
                )}
            </Box>
        );
    };

    // Table component with status badge
    const TableComponent = ({ table }) => {
        const seats = table.seats || [];

        // Determine table status
        const getTableStatus = () => {
            if (!table.isActive) return { label: 'Closed', color: 'var(--cm-text-muted)' };
            if (table.occupiedSeats === table.totalSeats) return { label: 'Full', color: 'var(--cm-danger)' };
            if (table.occupiedSeats > 0) return { label: 'Active', color: 'var(--cm-success)' };
            return { label: 'Open', color: 'var(--cm-info)' };
        };

        const tableStatus = getTableStatus();
        // A private booking lapses on its own once its end time passes
        const isPrivateNow =
            Boolean(table.isPrivate) && (!table.privateUntil || new Date(table.privateUntil) > new Date());

        return (
            <Box
                className="table-card"
                sx={{
                    position: 'relative',
                    width: '100%',
                    // Fit the screen height too (top bar + padding take ~76px)
                    height: 'min(clamp(210px, 34vw, 340px), calc(100dvh - 76px))',
                    minHeight: 180,
                    px: 'clamp(12px, 4vw, 40px)',
                    py: 'clamp(12px, 2.5vw, 24px)',
                }}
            >
                {/* Oval poker table surface */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 'clamp(20px, 4vw, 40px) clamp(40px, 10vw, 90px)',
                        borderRadius: '50%',
                        background: 'radial-gradient(ellipse at center, #168047 0%, #086132 72%, #064626 100%)',
                        border: 'clamp(6px, 1.2vw, 12px) solid #6b3f22',
                        boxShadow:
                            'inset 0 0 0 3px #9a6339, inset 0 0 28px rgba(0,0,0,0.45), 0 10px 20px rgba(15,23,42,0.35)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: '13%',
                            borderRadius: '50%',
                            border: '2px solid rgba(110,231,183,0.22)',
                            boxShadow: 'inset 0 0 16px rgba(0,0,0,0.2)',
                        },
                        '&::after': {
                            content: '"♠"',
                            position: 'absolute',
                            right: '15%',
                            bottom: '12%',
                            color: 'rgba(255,255,255,0.08)',
                            fontSize: 'clamp(28px, 5.5vw, 56px)',
                            fontWeight: 900,
                        },
                    }}
                >
                    <Box
                        sx={{
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 'clamp(150px, 26vw, 240px)',
                            maxWidth: '70%',
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                            bgcolor: 'rgba(2,44,27,0.46)',
                            border: '1px solid rgba(167,243,208,0.18)',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.18)',
                            backdropFilter: 'blur(2px)',
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.55)',
                                fontSize: 'clamp(8px, 1vw, 10px)',
                                fontWeight: 800,
                                letterSpacing: 1.5,
                                textTransform: 'uppercase',
                            }}
                        >
                            Table {table.tableNumber}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography sx={{ fontSize: 'clamp(14px, 2.2vw, 19px)', fontWeight: 800 }}>
                                {table.tableName}
                            </Typography>
                            <Chip
                                label={tableStatus.label}
                                size="small"
                                sx={{
                                    backgroundColor: tableStatus.color,
                                    color: 'white',
                                    fontSize: '0.62rem',
                                    height: 19,
                                }}
                            />
                            {isPrivateNow && (
                                <Chip
                                    label="Private"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'var(--cm-warning)',
                                        color: 'white',
                                        fontSize: '0.62rem',
                                        fontWeight: 700,
                                        height: 19,
                                    }}
                                />
                            )}
                        </Box>
                        {isPrivateNow && table.privateUntil && (
                            <Typography sx={{ color: '#fdba74', fontSize: 'clamp(9px, 1.2vw, 11px)', fontWeight: 700, mb: 0.25 }}>
                                Booked private until {new Date(table.privateUntil).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                            </Typography>
                        )}
                        <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 'clamp(10px, 1.3vw, 12px)', fontWeight: 500, mb: 0.25 }}>
                            {table.location || 'Location not set'}
                        </Typography>
                        <Typography
                            sx={{
                                color: '#fde68a',
                                fontSize: 'clamp(9px, 1.2vw, 11px)',
                                fontWeight: 700,
                                mb: 0.25,
                            }}
                        >
                            Dealer: {table.activeDealer?.dealer?.name || 'Not logged in'}
                        </Typography>
                        <TableTimer
                            runningSince={table.runningSince}
                            lastStoppedAt={table.lastStoppedAt}
                            lastRunMinutes={table.lastRunMinutes}
                        />
                        <Chip
                            label={`${table.occupiedSeats}/${table.totalSeats} players`}
                            size="small"
                            sx={{
                                backgroundColor: table.occupiedSeats === 0 ? '#22c55e' : '#3b82f6',
                                color: 'white',
                                fontWeight: 'bold',
                                height: 23,
                            }}
                        />
                    </Box>
                </Box>

                {/* Seats and players distributed around the table edge */}
                {seats.map((seat, index) => {
                    const angle = -90 + (index * 360) / seats.length;
                    const radians = (angle * Math.PI) / 180;
                    const left = 50 + 44 * Math.cos(radians);
                    const top = 50 + 42 * Math.sin(radians);

                    return (
                        <Box
                            key={seat._id}
                            sx={{
                                position: 'absolute',
                                left: `${left}%`,
                                top: `${top}%`,
                                transform: 'translate(-50%, -50%)',
                                zIndex: 2,
                            }}
                        >
                            <SeatComponent seat={seat} table={table} />
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Device Constraint Overlays
    if (!checkedDevice) return null; // Wait for check

    if (!isMobile) {
        return (
            <Box
                sx={{
                    height: '100vh',
                    width: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: '#1c1c20',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                }}
            >
                <PhoneIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Mobile Device Required
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8, maxWidth: 600 }}>
                    The Dealer Management tool is designed for mobile devices only. Please access this page from a phone or tablet.
                </Typography>
            </Box>
        );
    }

    // The table list works in portrait; only an opened table needs landscape.
    if (!isLandscape && selectedTableId) {
        return (
            <Box
                sx={{
                    height: '100vh',
                    width: '100vw',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: '#141416',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                }}
            >
                <RotateIcon sx={{ fontSize: 80, mb: 2, animation: 'spin 2s infinite linear' }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Please Rotate Your Device
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8 }}>
                    Rotate your device to landscape to run this table.
                </Typography>
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            25% { transform: rotate(-90deg); }
                            100% { transform: rotate(-90deg); }
                        }
                    `}
                </style>
            </Box>
        );
    }

    if (loading) {
        // Loading Skeleton for better UX - mirrors the real dark floor view (gradient
        // header, color legend, oval felt tables with seats around the rim) instead of
        // a generic list, so there's no jarring swap once real data arrives.
        const skeletonSeatCount = 6;
        return (
            <Box sx={{
                // Same viewport pinning as the floor view below
                position: 'fixed',
                inset: 0,
                backgroundColor: '#120b08',
                backgroundImage:
                    'radial-gradient(circle at 50% 20%, rgba(120,65,30,0.32), transparent 48%), repeating-linear-gradient(90deg, rgba(255,255,255,0.015) 0, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 38px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Skeleton Header */}
                <Box sx={{
                    background: '#141416',
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.15,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(201,164,92,0.45)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Skeleton variant="circular" width={38} height={38} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
                        <Box>
                            <Skeleton variant="text" width={90} height={12} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
                            <Skeleton variant="text" width={130} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.16)' }} />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
                        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
                    </Box>
                </Box>

                {/* Skeleton Legend */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    py: 0.75,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    borderBottom: '1px solid rgba(243,234,219,0.12)',
                }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Skeleton variant="circular" width={10} height={10} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
                            <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
                        </Box>
                    ))}
                </Box>

                {/* Skeleton Table Cards - oval felt + seats around the rim, same geometry as the real table */}
                <Box sx={{ flex: 1, px: 1.5, pb: 1.5, pt: 4, display: 'flex', flexDirection: 'column', gap: 3.5, overflow: 'hidden' }}>
                    {[1, 2].map((i) => (
                        <Box key={i} sx={{ position: 'relative', width: '100%', height: 'clamp(210px, 34vw, 340px)', px: 'clamp(12px, 4vw, 40px)', py: 'clamp(12px, 2.5vw, 24px)' }}>
                            {/* Oval felt */}
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    position: 'absolute',
                                    inset: 'clamp(20px, 4vw, 40px) clamp(40px, 10vw, 90px)',
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.06)',
                                }}
                            />
                            {/* Seats distributed around the rim, same angle math as the real seats */}
                            {Array.from({ length: skeletonSeatCount }).map((_, index) => {
                                const angle = -90 + (index * 360) / skeletonSeatCount;
                                const radians = (angle * Math.PI) / 180;
                                const left = 50 + 44 * Math.cos(radians);
                                const top = 50 + 42 * Math.sin(radians);
                                return (
                                    <Skeleton
                                        key={index}
                                        variant="circular"
                                        sx={{
                                            position: 'absolute',
                                            left: `${left}%`,
                                            top: `${top}%`,
                                            width: 'clamp(36px, 6vw, 56px)',
                                            height: 'clamp(36px, 6vw, 56px)',
                                            transform: 'translate(-50%, -50%)',
                                            bgcolor: 'rgba(255,255,255,0.14)',
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            // Pinned to the viewport (not 100vh, which is taller than the visible
            // area on phones): the page itself never scrolls, so nothing that
            // overflows can push the view off-screen. Only the tables list scrolls.
            position: 'fixed',
            inset: 0,
            backgroundColor: '#120b08',
            backgroundImage:
                'radial-gradient(circle at 50% 20%, rgba(120,65,30,0.32), transparent 48%), repeating-linear-gradient(90deg, rgba(255,255,255,0.015) 0, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 38px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Container doesn't scroll
        }}>
            {/* Floating top bar: Refresh & Logout (left), Color Legend (right) */}
            <Box sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                zIndex: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
            }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedTable && (
                        <Tooltip title="All tables">
                            <IconButton
                                size="small"
                                onClick={closeTable}
                                sx={{ color: '#f3eadb', bgcolor: 'rgba(20,20,22,0.72)', border: '1px solid rgba(243,234,219,0.25)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(59,130,246,0.35)' } }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!selectedTable && (
                        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.05rem', ml: 0.5, mr: 0.5 }}>
                            My Tables
                        </Typography>
                    )}
                    {!isFullscreen && (
                        <Tooltip title="Refresh floor">
                            <IconButton
                                size="small"
                                onClick={() => { fetchDashboard(); fetchTables(); fetchPlayerSessions(); }}
                                sx={{ color: '#f3eadb', bgcolor: 'rgba(20,20,22,0.72)', border: '1px solid rgba(243,234,219,0.25)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(59,130,246,0.35)' } }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                        <IconButton
                            size="small"
                            onClick={handleToggleFullscreen}
                            sx={{ color: '#f3eadb', bgcolor: 'rgba(20,20,22,0.72)', border: '1px solid rgba(243,234,219,0.25)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(59,130,246,0.35)' } }}
                        >
                            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    {!isFullscreen && (
                        <Tooltip title="Logout">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    dispatch(logout());
                                    navigate('/login');
                                    toast.success('Logged out successfully');
                                }}
                                sx={{ color: 'var(--cm-danger)', bgcolor: 'rgba(20,20,22,0.72)', border: '1px solid rgba(239,68,68,0.3)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(239,68,68,0.35)' } }}
                            >
                                <LogoutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {selectedTable && !isFullscreen && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 5,
                        bgcolor: 'rgba(20,20,22,0.72)',
                        border: '1px solid rgba(243,234,219,0.25)',
                        backdropFilter: 'blur(4px)',
                        color: 'rgba(255,255,255,0.85)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--cm-success)' }} />
                            <Typography sx={{ fontSize: '0.65rem' }}>Available</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--cm-info)' }} />
                            <Typography sx={{ fontSize: '0.65rem' }}>Occupied</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--cm-warning)' }} />
                            <Typography sx={{ fontSize: '0.65rem' }}>Paused</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'var(--cm-warning)' }} />
                            <Typography sx={{ fontSize: '0.65rem' }}>Reserved</Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Tables - Full Width Scrollable */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    px: 1.5,
                    pb: 1.5,
                    // Extra headroom so the topmost seat's elapsed-time badge (which floats
                    // above the seat circle) clears the floating top bar (refresh/logout + legend)
                    // instead of being clipped or overlapped by it.
                    pt: 7,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
                    // Hide scrollbar on mobile for cleaner look
                    '&::-webkit-scrollbar': {
                        width: '0px',
                        display: 'none',
                    },
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                }}
            >
                {/* Pull-down indicator */}
                {pullDistance > 0 && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 1,
                        transform: `translateY(${Math.min(pullDistance / 2, 40)}px)`,
                        opacity: Math.min(pullDistance / pullThreshold, 1),
                        transition: 'opacity 0.15s ease-out'
                    }}>
                        <Box sx={{
                            transform: `rotate(${Math.min(pullDistance * 2, 180)}deg)`,
                            transition: 'transform 0.1s ease-out'
                        }}>
                            <PullIcon sx={{
                                color: pullDistance >= pullThreshold ? '#22c55e' : '#a39c90',
                                fontSize: 28
                            }} />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                ml: 1,
                                color: pullDistance >= pullThreshold ? '#22c55e' : '#a39c90'
                            }}
                        >
                            {pullDistance >= pullThreshold ? 'Release to refresh' : 'Pull down to refresh'}
                        </Typography>
                    </Box>
                )}

                {/* Pull-to-refresh indicator */}
                {isRefreshing && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                        gap: 1
                    }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="textSecondary">Refreshing...</Typography>
                    </Box>
                )}

                {selectedTable ? (
                    <TableComponent table={selectedTable} />
                ) : selectedTableId ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <DealerTableGrid
                        tables={tables}
                        currentUserId={userInfo?.id || userInfo?._id}
                        search={tableSearch}
                        onSearchChange={setTableSearch}
                        onOpenTable={openTable}
                        // onTransferTable={setTableToTransfer} // table transfer disabled
                    />
                )}
            </Box>



            {/* Table transfer disabled: not needed for now
            <TableTransferDialog
                open={Boolean(tableToTransfer)}
                table={tableToTransfer}
                apiUrl={API_URL}
                token={userInfo?.token}
                onClose={() => setTableToTransfer(null)}
                onTransferred={handleTableTransferred}
            />
            */}

            {/* Search Player Modal */}
            <Dialog
                open={searchModalOpen}
                onClose={() => setSearchModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Search Player to Seat</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder="Search by phone, name, or ID..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            handleSearchPlayers(e.target.value);
                        }}
                        sx={{ mt: 1, mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchLoading && <CircularProgress size={20} />,
                        }}
                    />

                    {!searchLoading && searchQuery.length > 0 && searchQuery.length < 3 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Type at least 3 characters to search.
                        </Alert>
                    )}

                    {!searchLoading && searchQuery.length >= 3 && searchResults.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {userInfo?.role?.isSuperAdmin
                                ? `No verified players found for "${searchQuery}".`
                                : `No verified players checked in at this club found for "${searchQuery}".`}
                        </Alert>
                    )}

                    {!searchLoading && searchQuery.length === 0 && searchResults.length === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {userInfo?.role?.isSuperAdmin
                                ? 'Only verified players are shown.'
                                : 'Only verified players checked in at this club today are shown.'}
                        </Alert>
                    )}

                    {searchResults.map((player) => (
                        <Paper
                            key={player._id}
                            sx={{
                                p: 2,
                                mb: 1,
                                cursor: (player.isSeated || player.ban?.isBanned || !player.securityCheckedIn) ? 'not-allowed' : 'pointer',
                                opacity: (player.isSeated || player.ban?.isBanned || !player.securityCheckedIn) ? 0.6 : 1,
                                backgroundColor: player.ban?.isBanned
                                    ? 'var(--cm-danger-soft)'
                                    : !player.securityCheckedIn
                                        ? 'var(--cm-warning-soft)'
                                        : 'inherit',
                                '&:hover': {
                                    backgroundColor: (player.isSeated || player.ban?.isBanned || !player.securityCheckedIn)
                                        ? (player.ban?.isBanned ? 'var(--cm-danger-soft)' : !player.securityCheckedIn ? 'var(--cm-warning-soft)' : 'inherit')
                                        : 'var(--cm-surface-2)',
                                },
                            }}
                            onClick={() => {
                                if (player.ban?.isBanned) {
                                    toast.error('Player is banned and cannot be seated');
                                    return;
                                }
                                if (!player.securityCheckedIn) {
                                    toast.error('Player has not completed security check-in today');
                                    return;
                                }
                                if (!player.isSeated) {
                                    handleInitiateSeat(player);
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar>{player.name?.[0]?.toUpperCase() || 'U'}</Avatar>
                                    <Box>
                                        <Typography fontWeight="bold">
                                            {player.name || 'Unknown'}
                                            {player.ban?.isBanned && <span style={{ color: 'red', fontSize: '0.8rem', marginLeft: '8px' }}>(Banned)</span>}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {player.phoneNumber}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {player.ban?.isBanned ? (
                                        <Chip icon={<CancelIcon />} label="Banned" color="error" size="small" />
                                    ) : player.verification?.isFullyVerified ? (
                                        <Chip icon={<VerifiedIcon />} label="Verified" color="success" size="small" />
                                    ) : (
                                        <Chip icon={<WarningIcon />} label="Not Verified" color="warning" size="small" />
                                    )}
                                    {player.securityCheckedIn ? (
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label="Security OK"
                                            size="small"
                                            sx={{ backgroundColor: 'var(--cm-success-soft)', color: 'var(--cm-success)' }}
                                        />
                                    ) : (
                                        <Chip
                                            icon={<CancelIcon />}
                                            label="No Security"
                                            size="small"
                                            sx={{ backgroundColor: 'var(--cm-warning-soft)', color: 'var(--cm-warning)' }}
                                        />
                                    )}
                                    {player.isSeated && (
                                        <Chip label="Already Seated" color="error" size="small" />
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                            setSearchModalOpen(false);
                            handleOpenRegistrationModal();
                        }}
                    >
                        Register New Player
                    </Button>
                    <Button onClick={() => setSearchModalOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Registration Modal */}
            <Dialog
                open={registerModalOpen}
                onClose={handleCloseRegistrationModal}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {registerStep === 1 ? 'Register New Player' : 'Verify Phone Number'}
                </DialogTitle>
                <DialogContent>
                    {registerStep === 1 ? (
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                label="Phone Number"
                                fullWidth
                                placeholder="9876543210"
                                value={registerData.phoneNumber}
                                onChange={handleRegisterPhoneChange}
                                inputProps={{ inputMode: 'numeric' }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Full Name"
                                fullWidth
                                placeholder="Player Name"
                                value={registerData.name}
                                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                OTP sent to {registerData.phoneNumber}
                            </Typography>
                            <TextField
                                label="Enter OTP"
                                fullWidth
                                value={registerData.otp}
                                onChange={(e) => setRegisterData({ ...registerData, otp: e.target.value })}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRegistrationModal}>Cancel</Button>
                    {registerStep === 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleInitiateRegistration}
                            disabled={registerData.phoneNumber.length !== 10 || !registerData.name.trim()}
                        >
                            Send OTP
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleConfirmRegistration}
                            disabled={!registerData.otp}
                        >
                            Verify & Register
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Player Details Modal */}
            <Dialog
                open={playerDetailModalOpen}
                onClose={() => setPlayerDetailModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { maxWidth: 340 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Player Details
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    {playerVerification && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Avatar sx={{ width: 52, height: 52, bgcolor: 'var(--cm-info)' }}>
                                {playerVerification.player?.name?.[0]?.toUpperCase() || 'U'}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                                    {playerVerification.player?.name}
                                </Typography>
                                {playerVerification.ban?.isBanned && (
                                    <Chip icon={<CancelIcon />} label="Banned" color="error" size="small" sx={{ mt: 0.5 }} />
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        display: 'grid',
                        gridAutoFlow: 'column',
                        gridAutoColumns: '1fr',
                        gap: 1,
                    }}
                >
                    {selectedPlayer && playerSessions && (
                        (!playerSessions[selectedPlayer._id] || playerSessions[selectedPlayer._id].status === 'completed') ? (
                            <Tooltip title="Start Session">
                                <Box sx={{ textAlign: 'center' }}>
                                    <IconButton
                                        onClick={() => handleControlSession(selectedPlayer._id, 'start')}
                                        sx={{ bgcolor: 'success.main', color: '#fff', '&:hover': { bgcolor: 'success.dark' } }}
                                    >
                                        <StartIcon />
                                    </IconButton>
                                    <Typography variant="caption" display="block">Start</Typography>
                                </Box>
                            </Tooltip>
                        ) : playerSessions[selectedPlayer._id].status === 'paused' ? (
                            <Tooltip title="Resume Session">
                                <Box sx={{ textAlign: 'center' }}>
                                    <IconButton
                                        onClick={() => handleControlSession(selectedPlayer._id, 'resume')}
                                        sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                                    >
                                        <StartIcon />
                                    </IconButton>
                                    <Typography variant="caption" display="block">Resume</Typography>
                                </Box>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Pause Session">
                                <Box sx={{ textAlign: 'center' }}>
                                    <IconButton
                                        onClick={() => handleControlSession(selectedPlayer._id, 'pause')}
                                        sx={{ bgcolor: 'warning.main', color: '#fff', '&:hover': { bgcolor: 'warning.dark' } }}
                                    >
                                        <PauseIcon />
                                    </IconButton>
                                    <Typography variant="caption" display="block">Pause</Typography>
                                </Box>
                            </Tooltip>
                        )
                    )}

                    <Tooltip title="Add Chips">
                        <Box sx={{ textAlign: 'center' }}>
                            <IconButton
                                onClick={handleInitiateRebuyFromDetails}
                                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                            >
                                <MoneyIcon />
                            </IconButton>
                            <Typography variant="caption" display="block">Add Chips</Typography>
                        </Box>
                    </Tooltip>

                    <Tooltip title="Stop Session">
                        <Box sx={{ textAlign: 'center' }}>
                            <IconButton
                                onClick={handleStopSessionFromDetails}
                                sx={{ bgcolor: 'error.main', color: '#fff', '&:hover': { bgcolor: 'error.dark' } }}
                            >
                                <StopIcon />
                            </IconButton>
                            <Typography variant="caption" display="block">Stop</Typography>
                        </Box>
                    </Tooltip>

                    <Tooltip title="Close">
                        <Box sx={{ textAlign: 'center' }}>
                            <IconButton
                                onClick={() => {
                                    setPlayerDetailModalOpen(false);
                                    setSelectedSeatContext(null);
                                }}
                                sx={{
                                    bgcolor: 'grey.200',
                                    color: 'grey.900',
                                    '&:hover': { bgcolor: 'grey.400', color: 'grey.900' },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography variant="caption" display="block">Close</Typography>
                        </Box>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Buy-In Modal -> Start Session Modal */}
            <Dialog open={buyInModalOpen} onClose={() => setBuyInModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Start Session</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Seat <strong>{pendingPlayerToSeat?.name}</strong> and start session timer?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBuyInModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmSeat} color="primary">
                        Confirm & Start
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cash-Out Modal -> Stop Session Modal */}
            <Dialog open={cashOutModalOpen} onClose={() => setCashOutModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Stop Session</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Stop session for: <strong>{seatToCashOut?.playerName}</strong>?
                    </Alert>
                    <Typography variant="body2">
                        This will free the seat and stop the timer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCashOutModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmCashOut} color="error">
                        Stop Session
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rebuy Modal -> Add Chips */}
            <Dialog
                open={rebuyModalOpen}
                onClose={() => {
                    setRebuyModalOpen(false);
                    setSeatToRebuy(null);
                    setTransactionAmount('');
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Add Chips</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Add chips for: <strong>{seatToRebuy?.playerName}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={transactionAmount}
                        onChange={(e) => setTransactionAmount(e.target.value)}
                        inputProps={{ min: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setRebuyModalOpen(false);
                            setSeatToRebuy(null);
                            setTransactionAmount('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmRebuy}
                        color="primary"
                        disabled={!transactionAmount || parseFloat(transactionAmount) <= 0}
                    >
                        Confirm Add Chips
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default DealerManagement;
